import React from 'react';
import { cn } from '../../../lib/utils';
import { OrkaSurface } from '../OrkaSurface';
import { OrkaText } from '../OrkaText';
import { OrkaIcon } from '../OrkaIcon';
import { getIntent } from '../../../design-system/intents';

/**
 * OrkaPane — canonical pane container (PRD §5.5).
 *
 * Replaces the three near-identical shells in BacklogPane, ScheduledPane,
 * Timeline (and any future pane). Header band + collapsible filter slot +
 * scrollable body. Pane intent drives icon, accent, and empty-state copy.
 *
 *   <OrkaPane
 *     title="Backlog"
 *     intent="capture"
 *     icon={Inbox}
 *     stats={{ effort: 124, total: 41, filtered: 12 }}
 *     filterSlot={<MyFilters />}
 *     bordered
 *     onDragOver={...}
 *     onDrop={...}
 *     borderColor="urgency-amber"  // optional override (e.g. Daily Dashboard urgency border)
 *   >
 *     {tasks.map(...)}
 *   </OrkaPane>
 */
export function OrkaPane({
  title,
  subtitle,
  intent = 'plan',
  icon: Icon,
  stats,
  filterSlot,
  filterActive = false,
  rightSlot,
  bordered = true,
  density = 'standard',
  className,
  bodyClassName,
  borderTone,  // 'normal' | 'green' | 'blue' | 'amber' | 'red'  — overrides default border
  onDragOver,
  onDragLeave,
  onDrop,
  isDropTarget = false,
  testId,
  children,
}) {
  const intentMeta = getIntent(intent);
  const URGENCY = {
    normal: undefined,
    green: '#10B981',
    blue: '#3B82F6',
    amber: '#F59E0B',
    red: '#EF4444',
  };
  const urgencyColor = borderTone ? URGENCY[borderTone] : undefined;

  return (
    <OrkaSurface
      variant="raised"
      radius="xl"
      bordered={bordered}
      className={cn('orka-pane h-full flex flex-col overflow-hidden', className)}
      style={urgencyColor ? { borderColor: urgencyColor, borderWidth: 2 } : undefined}
      data-intent={intent}
      data-testid={testId}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Header band */}
      <header className="px-4 py-3 border-b orka-border-subtle flex items-center gap-3">
        {Icon && <OrkaIcon icon={Icon} size="md" intent={intentMeta.key === 'capture' ? 'tertiary' : 'intent'} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <OrkaText size="h3" variant="primary" truncate>{title}</OrkaText>
            {filterActive && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider"
                style={{ backgroundColor: '#F59E0B22', color: '#D97706' }}
              >
                Filtered
              </span>
            )}
          </div>
          {subtitle && <OrkaText size="micro" variant="tertiary" as="div" className="mt-0.5 normal-case tracking-normal">{subtitle}</OrkaText>}
        </div>
        {/* Stats strip — canonical right-side meta */}
        {stats && (
          <div className="flex items-center gap-2 shrink-0 text-xs orka-text-secondary font-mono">
            {stats.effort != null && (
              <span className="px-1.5 h-6 inline-flex items-center rounded-md" style={{ backgroundColor: 'var(--orka-surface-subtle)' }}>
                E:{stats.effort}
              </span>
            )}
            {stats.duration && (
              <span className="px-1.5 h-6 inline-flex items-center">{stats.duration}</span>
            )}
            {stats.filtered != null && stats.total != null && (
              <span className="px-1.5 h-6 inline-flex items-center">{stats.filtered}/{stats.total}</span>
            )}
          </div>
        )}
        {rightSlot}
      </header>

      {/* Filter slot */}
      {filterSlot && (
        <div className="px-4 py-2 border-b orka-border-subtle">
          {filterSlot}
        </div>
      )}

      {/* Body */}
      <div
        className={cn(
          'flex-1 overflow-y-auto',
          density === 'compact' ? 'p-2' : 'p-3',
          isDropTarget && 'orka-surface-subtle',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </OrkaSurface>
  );
}

export default OrkaPane;
